import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  createCompetency,
  getAllCompetenciesV2,
  updateCompetency,
  deleteCompetency,
  archiveCompetency,
} from "../../api/apiCall";
import { useAuth } from "../../auth/auth";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { Modal } from "antd";

const Competencies = () => {
  const { state } = useAuth();
  const [competencies, setCompetencies] = useState<any>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [currentCompetency, setCurrentCompetency] = useState<any>(null);
  const [competencyName, setCompetencyName] = useState("");
  const [competencyDescription, setCompetencyDescription] = useState("");
  const [formError, setFormError] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const filteredCompetencies = showArchived
    ? competencies
    : competencies.filter((c: any) => !c.isArchived);

  const handleAddClick = () => {
    setModalMode("add");
    setCompetencyName("");
    setCompetencyDescription("");
    setCurrentCompetency(null);
    setFormError("");
    setIsDialogOpen(true);
  };

  const handleEditClick = (params: any) => {
    setModalMode("edit");
    setCurrentCompetency(params.row);
    setCompetencyName(params.row.competencyName);
    setCompetencyDescription(params.row.description);
    setFormError("");
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setCompetencyName("");
    setCompetencyDescription("");
    setCurrentCompetency(null);
    setFormError("");
  };

  const handleSubmit = async () => {
    if (!competencyName.trim()) {
      setFormError("Competency name cannot be empty");
      return;
    }

    if (modalMode === "add") {
      const response = await createCompetency(state.user?.token, {
        competencyName,
        description: competencyDescription,
      });
      if (response) {
        toast.success(response.data);
        getAllCompetenciesV2(state.user?.token).then(setCompetencies);
        handleDialogClose();
      } else {
        toast.error("Something went wrong!");
      }
    } else {
      if (currentCompetency) {
        console.log("Editing competency:", currentCompetency.id, competencyName);
        const response = await updateCompetency(
          state.user?.token,
          currentCompetency.id,
          competencyName,
          competencyDescription
        );
        if (response) {
          toast.success(response.data);
          getAllCompetenciesV2(state.user?.token).then(setCompetencies);
          handleDialogClose();
        } else {
          toast.error("Something went wrong!");
        }
      }
    }
  };

  useEffect(() => {
    getAllCompetenciesV2(state.user?.token).then(setCompetencies);
  }, []);

  const columns: any = [
    {
      field: "competencyName",
      headerName: "Competency",
      flex: 0.7,
      renderCell: (params: any) => <div>{params.row.competencyName}</div>,
    },
    {
      field: "isArchived",
      headerName: "Is Archived",
      flex: 0.3,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => <div>{params.row.isArchived ? "Yes" : "No"}</div>,
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      flex: 0.3,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => (
        <div>{format(new Date(params.row.createdDate || Date.now()), "dd/MM/yyyy")}</div>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => (
        <div>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params)}
            style={{ marginRight: 8 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            style={{ marginRight: 8 }}
            onClick={() =>
              archiveCompetency(
                state.user?.token,
                params.row.id,
                params.row.isArchived ? false : true
              )
                .then((res: any) => {
                  if (res.success === false) {
                    toast.error(res.message);
                    return;
                  }
                  getAllCompetenciesV2(state.user?.token).then(setCompetencies);
                  toast.success("Competency archived successfully");
                }
              )
            }
          >
            {params.row.isArchived ? "Unarchive" : "Archive"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() =>
              Modal.confirm({
                title: `Are you sure you want to delete ${params.row.competencyName}?`,
                onOk: () =>
                  deleteCompetency(state.user?.token, params.row.id)
                    .then((res: any) => {
                      if (res.success === false) {
                          toast.error(res.message);
                          return;
                      }

                      getAllCompetenciesV2(state.user?.token).then(setCompetencies);
                      toast.success("Competency deleted successfully");
                    }),
                okButtonProps: { danger: true },
                okText: "YES",
                cancelText: "NO",
                centered: true,
              })
            }
            style={{ marginRight: 8 }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: 1,
        height: "100%",
        minHeight: "90vh",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          maxWidth: "1600px",
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                color="primary"
              />
            }
            label={showArchived ? "Showing Archived" : "Hiding Archived"}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddClick}
            style={{ boxShadow: "none" }}
          >
            Add Competency
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: "740px",
            margin: "0 auto",
            "@media (max-width: 800px)": {
              overflowX: "auto",
              margin: 1,
              minWidth: "95%",
            },
          }}
        >
          <DataGrid
            rows={filteredCompetencies}
            columns={columns}
            disableRowSelectionOnClick
            sx={{
              "@media (max-width: 700px)": {
                minWidth: "100%",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#333",
                fontWeight: "bold",
                fontSize: 15,
                backgroundColor: "#f0f0f0",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f1f1f1",
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalMode === "add" ? "Add New Competency" : "Edit Competency"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Competency Name"
            type="text"
            fullWidth
            size="small"
            variant="outlined"
            value={competencyName}
            onChange={(e) => {
              setCompetencyName(e.target.value);
              setFormError("");
            }}
            error={!!formError}
            helperText={formError}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              }
            }}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Competency Description"
            type="text"
            fullWidth
            size="small"
            variant="outlined"
            value={competencyDescription}
            onChange={(e) => {
              setCompetencyDescription(e.target.value);
              setFormError("");
            }}
            error={!!formError}
            helperText={formError}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              }
            }}
            sx={{ mt: 1, mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            style={{ boxShadow: "none" }}
          >
            {modalMode === "add" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Competencies;
